import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  alpha,
  Chip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Menu as MenuIcon,
  Palette as PaletteIcon,
  Token as TokenIcon,
  SupportAgent as SupportIcon,
  ExpandMore,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { UserContext } from "../../context/UserContext";
import ThemeToggle from "../../common/ThemeToggle";
import ThemeDrawer from "./ThemeDrawer";

import { SIDEBAR_W, LANG, CHAT_MAX_W } from "./chat/constants";
import Sidebar from "./chat/Sidebar";
import WelcomeScreen from "./chat/WelcomeScreen";
import MessageBubble from "./chat/MessageBubble";
import ModelPicker from "./chat/ModelPicker";
import Composer from "./chat/Composer";
import Dialogs from "./chat/Dialogs";
import UserSupportTickets from "../support/UserSupportTickets";
import CommonDialog from "../../common/CommonDialog";
import PopupRenderer from "../popups/PopupRenderer";

const UserDashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hitAxios } = useContext(GlobalContext);
  const { lang } = useContext(TranslateContext);
  const { userData, updateUserData, setUserData } = useContext(UserContext);
  const [supportOpen, setSupportOpen] = useState(false);

  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("claude_4_6_sonnet");
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [webAccess, setWebAccess] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [search, setSearch] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [themeDrawerOpen, setThemeDrawerOpen] = useState(false);
  const [activeUserMessageId, setActiveUserMessageId] = useState(null);

  const [webHash, setWebHash] = React.useState(null);
  const webHashRef = React.useRef(null);
  const initialLoadDone = useRef(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, conv: null });
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    conv: null,
    value: "",
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const streamingMsgRef = useRef("");
  const abortRef = useRef(false);
  const messagesAreaRef = useRef(null);
  const userScrolledUp = useRef(false);
  const messageRefs = useRef({});

  const scrollToBottom = useCallback((force = false) => {
    if (force || !userScrolledUp.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const userMessages = React.useMemo(() => {
    return messages
      .map((msg, idx) => {
        const rawId = msg.id || idx;

        return {
          msg,
          idx,
          domId: `user-msg-${rawId}`,
        };
      })
      .filter(({ msg }) => msg.role === "user")
      .map(({ msg, idx, domId }) => ({
        id: msg.id || idx,
        domId,
        content: msg.content || "",
      }));
  }, [messages]);

  const updateActiveUserMessage = useCallback(() => {
    const container = messagesAreaRef.current;
    if (!container || !userMessages.length) {
      setActiveUserMessageId(null);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const anchorY = containerRect.top + 110;

    let closestId = null;
    let closestDistance = Infinity;

    userMessages.forEach((item) => {
      const el = messageRefs.current[item.domId];
      if (!el) return;

      const rect = el.getBoundingClientRect();

      const isVisible =
        rect.bottom >= containerRect.top && rect.top <= containerRect.bottom;

      if (!isVisible) return;

      const distance = Math.abs(rect.top - anchorY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = item.domId;
      }
    });

    if (closestId) {
      setActiveUserMessageId(closestId);
    }
  }, [userMessages]);

  const handleMessagesScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      const isNearBottom = distanceFromBottom < 80;

      userScrolledUp.current = !isNearBottom;
      setShowScrollBtn(!isNearBottom);

      updateActiveUserMessage();
    },
    [updateActiveUserMessage],
  );

  const jumpToUserMessage = useCallback((domId) => {
    const container = messagesAreaRef.current;
    const target = messageRefs.current[domId];

    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const top = container.scrollTop + targetRect.top - containerRect.top - 72;

    userScrolledUp.current = true;
    setShowScrollBtn(true);
    setActiveUserMessageId(domId);

    container.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  }, []);

  const jumpToTop = useCallback(() => {
    const container = messagesAreaRef.current;
    if (!container) return;

    userScrolledUp.current = true;
    setShowScrollBtn(true);

    container.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const fetchWebHash = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/chat/web-token",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });

    if (res?.data?.success) {
      setWebHash(res.data.hash);
      webHashRef.current = res.data.hash;
    }
  }, [hitAxios]);

  React.useEffect(() => {
    fetchWebHash();
    const interval = setInterval(fetchWebHash, 25000);
    return () => clearInterval(interval);
  }, [fetchWebHash]);

  useEffect(() => {
    scrollToBottom();
    requestAnimationFrame(updateActiveUserMessage);
  }, [messages, scrollToBottom, updateActiveUserMessage]);

  useEffect(() => {
    if (!webHash || initialLoadDone.current) return;
    initialLoadDone.current = true;
    loadModels();
    loadConversations();
  }, [webHash]);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  async function loadModels() {
    const res = await hitAxios({
      path: "/api/chat/models",
      post: false,
      showLoading: false,
      showSnackbar: false,
      headers: { "x-web-hash": webHashRef.current },
    });

    if (res?.data?.success) setModels(res.data.data);
  }

  async function loadConversations(q = "") {
    const path = q
      ? `/api/chat/conversations?search=${encodeURIComponent(q)}`
      : "/api/chat/conversations";

    const res = await hitAxios({
      path,
      post: false,
      showLoading: false,
      showSnackbar: false,
      headers: { "x-web-hash": webHashRef.current },
    });

    if (res?.data?.success) setConversations(res.data.data || []);
  }

  async function loadMessages(conv) {
    setLoadingMessages(true);
    setMessages([]);

    const res = await hitAxios({
      path: `/api/chat/messages/${conv.conversation_id}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
      headers: { "x-web-hash": webHashRef.current },
    });

    setLoadingMessages(false);

    if (res?.data?.success) {
      const sanitized = (res.data.data || []).map((msg) => ({
        ...msg,
        content: msg.content?.replace(/monica/gi, "Codeyon AI") || msg.content,
      }));

      setMessages(sanitized);

      if (res.data.conversation?.model_key) {
        setSelectedModel(res.data.conversation.model_key);
      }
    }
  }

  const handleSelectConv = useCallback(
    (conv) => {
      if (isStreaming) return;
      setActiveConv(conv);
      loadMessages(conv);
      userScrolledUp.current = false;
      setShowScrollBtn(false);
      setActiveUserMessageId(null);
      if (isMobile) setSidebarOpen(false);
    },
    [isStreaming, isMobile],
  );

  const handleNewChat = useCallback(() => {
    if (isStreaming) return;
    setActiveConv(null);
    setMessages([]);
    setInput("");
    setActiveUserMessageId(null);
    userScrolledUp.current = false;
    setShowScrollBtn(false);
    if (isMobile) setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isStreaming, isMobile]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;

    setInput("");
    abortRef.current = false;
    userScrolledUp.current = false;
    setShowScrollBtn(false);

    const userMsg = {
      id: Date.now(),
      role: "user",
      content: text,
      model_key: selectedModel,
      createdAt: new Date().toISOString(),
    };

    const assistantMsg = {
      id: Date.now() + 1,
      role: "assistant",
      content: "",
      model_key: selectedModel,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);
    streamingMsgRef.current = "";

    try {
      const TOKEN_KEY = process.env.REACT_APP_TOKEN + "_user";
      const token = localStorage.getItem(TOKEN_KEY);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/chat/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: text,
            conversationId: activeConv?.conversation_id || null,
            modelKey: selectedModel,
            webAccess,
            webHash: webHashRef.current,
          }),
        },
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "message";

      while (true) {
        if (abortRef.current) break;

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
            continue;
          }

          if (!line.startsWith("data: ")) continue;

          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const parsed = JSON.parse(dataStr);

            if (currentEvent === "error") {
              const errorMsg = parsed.noCredits
                ? lang?.noCreditsError ||
                  "You have no credits left. Please contact support."
                : parsed.limitReached
                  ? lang?.dailyLimitError ||
                    "Daily token limit reached. Please try again tomorrow."
                  : parsed.restricted
                    ? lang?.accountRestricted ||
                      "Your account has been restricted. Please contact support."
                    : parsed.msg || lang?.errorOccurred || LANG.errorOccurred;

              setMessages((prev) => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;

                if (updated[lastIdx]?.role === "assistant") {
                  updated[lastIdx] = {
                    ...updated[lastIdx],
                    content: errorMsg,
                  };
                }

                return updated;
              });

              currentEvent = "message";
              continue;
            }

            if (parsed.token !== undefined) {
              const cleanToken = parsed.token.replace(/monica/gi, "Codeyon AI");
              streamingMsgRef.current += cleanToken;
              const current = streamingMsgRef.current;

              setMessages((prev) => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;

                if (updated[lastIdx]?.role === "assistant") {
                  updated[lastIdx] = {
                    ...updated[lastIdx],
                    content: current,
                  };
                }

                return updated;
              });
            }

            if (parsed.conversationId) {
              const newConvId = parsed.conversationId;

              if (
                parsed.creditsRemaining !== null &&
                parsed.creditsRemaining !== undefined
              ) {
                setUserData((prev) => ({
                  ...prev,
                  credits: parsed.creditsRemaining,
                }));
              }

              setTimeout(() => {
                loadConversations(search).then(() => {
                  setActiveConv(
                    (prev) => prev || { conversation_id: newConvId },
                  );
                });
              }, 500);
            }

            currentEvent = "message";
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;

        if (updated[lastIdx]?.role === "assistant") {
          updated[lastIdx] = {
            ...updated[lastIdx],
            content: lang?.errorOccurred || LANG.errorOccurred,
          };
        }

        return updated;
      });
    } finally {
      setIsStreaming(false);
      streamingMsgRef.current = "";
      setTimeout(() => loadConversations(search), 1500);
      updateUserData();
    }
  }, [input, isStreaming, activeConv, selectedModel, webAccess, search, lang]);

  const handleDeleteConv = async () => {
    const conv = deleteDialog.conv;
    if (!conv) return;

    const res = await hitAxios({
      path: "/api/chat/conversation/delete",
      post: true,
      obj: {
        conversationId: conv.conversation_id,
        webHash: webHashRef.current,
      },
      showLoading: false,
    });

    if (res?.data?.success) {
      setConversations((prev) =>
        prev.filter((c) => c.conversation_id !== conv.conversation_id),
      );

      if (activeConv?.conversation_id === conv.conversation_id) {
        setActiveConv(null);
        setMessages([]);
      }
    }

    setDeleteDialog({ open: false, conv: null });
  };

  const handleRenameConv = async () => {
    const conv = renameDialog.conv;
    const title = renameDialog.value.trim();

    if (!conv || !title) return;

    const res = await hitAxios({
      path: "/api/chat/conversation/rename",
      post: true,
      obj: {
        conversationId: conv.conversation_id,
        title,
        webHash: webHashRef.current,
      },
      showLoading: false,
    });

    if (res?.data?.success) {
      setConversations((prev) =>
        prev.map((c) =>
          c.conversation_id === conv.conversation_id ? { ...c, title } : c,
        ),
      );

      if (activeConv?.conversation_id === conv.conversation_id) {
        setActiveConv((prev) => ({ ...prev, title }));
      }
    }

    setRenameDialog({ open: false, conv: null, value: "" });
  };

  useEffect(() => {
    const t = setTimeout(() => loadConversations(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const groupedConvs = React.useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);

    yesterday.setDate(yesterday.getDate() - 1);

    const groups = { today: [], yesterday: [], older: [] };

    conversations.forEach((c) => {
      const d = new Date(c.updatedAt || c.createdAt);
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      if (day.getTime() === today.getTime()) groups.today.push(c);
      else if (day.getTime() === yesterday.getTime()) groups.yesterday.push(c);
      else groups.older.push(c);
    });

    return groups;
  }, [conversations]);

  const sidebarProps = {
    lang,
    userData,
    isDark,
    search,
    setSearch,
    conversations,
    groupedConvs,
    activeConv,
    handleNewChat,
    handleSelectConv,
    setDeleteDialog,
    setRenameDialog,
    onSupportClick: () => setSupportOpen(true),
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        bgcolor: "background.default",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            width: sidebarOpen ? SIDEBAR_W : 0,
            flexShrink: 0,
            overflow: "hidden",
            transition: "width 0.2s ease",
          }}
        >
          <Sidebar {...sidebarProps} />
        </Box>
      )}

      {isMobile && (
        <Drawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "86vw", sm: SIDEBAR_W },
              maxWidth: SIDEBAR_W,
              bgcolor: "background.paper",
              border: "none",
            },
          }}
        >
          <Sidebar {...sidebarProps} />
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
          maxWidth: "100%",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: { xs: 1, sm: 2 },
            py: 1,
            gap: { xs: 0.75, sm: 1 },
            flexShrink: 0,
            minHeight: 58,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            boxShadow: isDark
              ? `0 1px 0 ${alpha(theme.palette.common.white, 0.04)}`
              : `0 1px 0 ${alpha(theme.palette.common.black, 0.04)}`,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Tooltip title={lang?.toggleSidebar || "Toggle sidebar"}>
            <IconButton
              size="small"
              onClick={() => setSidebarOpen((p) => !p)}
              sx={{
                color: "text.secondary",
                borderRadius: 1.5,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={lang?.newChat || LANG.newChat}>
            <IconButton
              size="small"
              onClick={handleNewChat}
              sx={{
                color: "text.secondary",
                borderRadius: 1.5,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          {models.length > 0 && (
            <Box
              sx={{
                minWidth: 0,
                maxWidth: { xs: 150, sm: 220 },
                display: "flex",
                flexShrink: 1,
              }}
            >
              <ModelPicker
                models={models}
                selectedModel={selectedModel}
                onSelect={setSelectedModel}
                lang={lang}
              />
            </Box>
          )}

          {userData?.dailyLimit > 0 && (
            <Tooltip title={lang?.tokensLeftToday || "Tokens left today"}>
              <Chip
                icon={
                  <TokenIcon
                    sx={{
                      fontSize: 13,
                      "&&": {
                        color:
                          userData?.tokensLeftToday === 0
                            ? theme.palette.error.main
                            : theme.palette.warning.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color:
                        userData?.tokensLeftToday === 0
                          ? "error.main"
                          : "text.primary",
                      fontSize: "0.72rem",
                    }}
                  >
                    {userData?.tokensLeftToday?.toLocaleString() ?? "∞"}{" "}
                    {lang?.todayLeft || "Today"}
                  </Typography>
                }
                size="small"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  height: 26,
                  borderRadius: 2,
                  border: `1px solid ${alpha(
                    userData?.tokensLeftToday === 0
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                    0.3,
                  )}`,
                  bgcolor: alpha(
                    userData?.tokensLeftToday === 0
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                    0.08,
                  ),
                  "& .MuiChip-label": { px: 0.75 },
                  "& .MuiChip-icon": { ml: 0.75 },
                }}
              />
            </Tooltip>
          )}

          <ThemeToggle size="small" />

          <Tooltip title={lang?.appearance || "Appearance"}>
            <IconButton
              size="small"
              onClick={() => setThemeDrawerOpen(true)}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: themeDrawerOpen
                  ? alpha(theme.palette.primary.main, 0.1)
                  : "transparent",
                color: themeDrawerOpen ? "primary.main" : "text.secondary",
                transition: "all 0.15s ease",
                flexShrink: 0,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: "primary.main",
                },
              }}
            >
              <PaletteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          ref={messagesAreaRef}
          onScroll={handleMessagesScroll}
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
            position: "relative",
            width: "100%",
            minWidth: 0,
            minHeight: 0,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.primary, 0.1),
              borderRadius: 3,
            },
          }}
        >
          {loadingMessages ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
              <CircularProgress size={24} sx={{ color: "primary.main" }} />
            </Box>
          ) : messages.length === 0 ? (
            <WelcomeScreen
              models={models}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              lang={lang}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: CHAT_MAX_W,
                mx: "auto",
                pt: { xs: 2, sm: 3 },
                pb: 2,
                px: { xs: 1.25, sm: 2, md: 3 },
                boxSizing: "border-box",
                minWidth: 0,
              }}
            >
              {messages.map((msg, idx) => {
                const rawId = msg.id || idx;
                const domId = msg.role === "user" ? `user-msg-${rawId}` : null;

                return (
                  <Box
                    key={rawId}
                    ref={(el) => {
                      if (!domId) return;

                      if (el) {
                        messageRefs.current[domId] = el;
                      } else {
                        delete messageRefs.current[domId];
                      }
                    }}
                    sx={{
                      scrollMarginTop: 84,
                    }}
                  >
                    <MessageBubble
                      message={msg}
                      lang={lang}
                      isStreaming={
                        isStreaming &&
                        idx === messages.length - 1 &&
                        msg.role === "assistant"
                      }
                    />
                  </Box>
                );
              })}

              <div ref={messagesEndRef} />
            </Box>
          )}

          {showScrollBtn && (
            <Box
              onClick={() => {
                userScrolledUp.current = false;
                setShowScrollBtn(false);
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{
                position: "sticky",
                bottom: 16,
                alignSelf: "flex-end",
                mr: { xs: 2, sm: 3 },
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "background.paper",
                border: `1.5px solid ${theme.palette.divider}`,
                boxShadow: isDark
                  ? `0 4px 16px ${alpha(theme.palette.common.black, 0.4)}`
                  : `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 10,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.main,
                  transform: "scale(1.08)",
                },
              }}
            >
              <ExpandMore sx={{ fontSize: 20, color: "primary.main" }} />
            </Box>
          )}
        </Box>

        <PopupRenderer lang={lang} />

        <Composer
          inputRef={inputRef}
          input={input}
          setInput={setInput}
          isStreaming={isStreaming}
          webAccess={webAccess}
          setWebAccess={setWebAccess}
          handleSend={handleSend}
          abortRef={abortRef}
          lang={lang}
        />
      </Box>

      <Dialogs
        lang={lang}
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        handleDeleteConv={handleDeleteConv}
        renameDialog={renameDialog}
        setRenameDialog={setRenameDialog}
        handleRenameConv={handleRenameConv}
      />

      <CommonDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        title={lang?.support || "Support"}
        icon={SupportIcon}
        maxWidth="xs"
        noMargin
      >
        <Box sx={{ height: 520 }}>
          <UserSupportTickets lang={lang} />
        </Box>
      </CommonDialog>

      <ThemeDrawer
        open={themeDrawerOpen}
        onClose={() => setThemeDrawerOpen(false)}
      />
    </Box>
  );
};

export default UserDashboard;
